
import { Navbar1 } from 'components/navbars';
import { Footer1 } from 'components/footer';

import Hero from './Hero';
import PostContent from './PostContent';

import { gallery } from '../Career/data';
import { useEffect, useState } from 'react';
import { Post2 } from 'components/blog';

import { useParams } from 'react-router-dom';
import { getBlogsBySlug } from 'helpers/api/BlogService';
import { Helmet } from 'react-helmet-async';
import config from 'config';

type Props = {
    blogData?: any; // Accept any data structure from DynamicPage
};


const BlogPost = ({ blogData: initialData }: Props) => {
    const { slug } = useParams();
    const [blogData, setBlogData] = useState<Post2 | null>(null);
    const [loading, setLoading] = useState(!initialData);

    useEffect(() => {
        // ✅ If initialData is provided, use it immediately (passed from DynamicPage)
        if (initialData) {
            console.log("BlogPost received initialData:", initialData);
            const transformedData: Post2 = {
                id: initialData.id,
                title: initialData.title || initialData.blogTitle,
                longDescription: initialData.longDescription || '',
                image: initialData.image || initialData.blogImage,
                tag: initialData.tag || initialData.blogTags,
                createdOn: initialData.createdOn,
                status: initialData.status,
                blogTitle: initialData.blogTitle,
                shortDescription: initialData.shortDescription,
                blogTags: initialData.blogTags,
                blogImage: initialData.blogImage,
                metaKeywords: initialData.metaKeywords,
                slug: initialData.slug
            };
            console.log("BlogPost transformed data:", transformedData);
            setBlogData(transformedData);
            setLoading(false);
        }
    }, [initialData]);

    useEffect(() => {
        // ✅ If no initial data but we have a slug, fetch from API
        if (!initialData && slug) {
            console.log("BlogPost fetching data for slug:", slug);
            setLoading(true);

            const fetchBlog = async () => {
                try {
                    const response = await getBlogsBySlug(slug);
                    console.log("BlogPost API response:", response);

                    if (response?.success && response.result) {
                        const blog: Post2 = {
                            id: response.result.id,
                            title: response.result.blogTitle,
                            longDescription: response.result.longDescription || '',
                            image: response.result.blogImage,
                            tag: response.result.blogTags,
                            createdOn: response.result.createdOn,
                            status: response.result.status,
                            blogTitle: response.result.blogTitle,
                            shortDescription: response.result.shortDescription,
                            blogTags: response.result.blogTags,
                            blogImage: response.result.blogImage,
                            metaKeywords: response.result.metaKeywords,
                            slug: response.result.slug
                        };

                        console.log("BlogPost setting blog data:", blog);
                        setBlogData(blog);
                    }
                    setLoading(false);
                } catch (error) {
                    console.error("Failed to fetch blog:", error);
                    setLoading(false);
                }
            };

            fetchBlog();
        }
    }, [slug, initialData]);

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
                <div className="text-center">
                    <div className="spinner-border mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading blog post...</p>
                </div>
            </div>
        );
    }

    if (!blogData) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
                <div className="text-center">
                    <h2>Blog post not found</h2>
                </div>
            </div>
        );
    }

    const canonicalSlug = slug || blogData.slug;
    const canonicalUrl = `${config.DOMAIN_URL}/${canonicalSlug}`;
    const description = blogData.shortDescription || "";
    const image = blogData.blogImage || blogData.image;

    return (
        <>
            {/* SEO Meta Tags */}
            <Helmet>
                <title>{`${blogData?.title} | Sales Trackify CRM & Sales Management`}</title>
                <link rel="canonical" href={canonicalUrl} />
                <meta
                    property="og:description"
                    content={description}
                />
                <meta
                    name="description"
                    content={description}
                />
                <meta name="robots" content="index, follow" />

                <meta
                    name="keywords"
                    content={blogData?.metaKeywords || blogData?.blogTags}
                />

                <meta property="og:title" content={blogData?.title} />

                <meta property="og:image" content={image} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blogData?.title} />
                <meta
                    name="twitter:description"
                    content={description}
                />
                <meta name="twitter:image" content={image} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={canonicalUrl} />
                {/* JSON-LD Schema for Article */}
                <script type="application/ld+json" dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BlogPosting',
                    'headline': blogData?.title,
                    'description': description,
                    'image': image,
                    'datePublished': blogData?.createdOn,
                    'author': {
                      '@type': 'Organization',
                      'name': 'SalesTrackify'
                    },
                    'publisher': {
                      '@type': 'Organization',
                      'name': 'SalesTrackify',
                      'logo': {
                        '@type': 'ImageObject',
                        'url': `${config.DOMAIN_URL}/logo.png`
                      }
                    },
                    'url': canonicalUrl
                  })
                }} />
            </Helmet>

            <div>
                <Navbar1
                    hideSearch
                    fixedWidth
                    navClass="navbar-light zindex-10 shadow"
                    buttonClass="btn-outline-secondary btn-sm"
                />
                <Hero post={blogData} />
            </div>

            <PostContent gallery={gallery} post={blogData} />

            <Footer1 />
        </>
    );
};

export default BlogPost;

